import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "../../components/common/Logo";

interface DocArticle {
  id: string;
  titleKey: string;
  content: React.ReactNode;
}

interface DocCategory {
  id: string;
  titleKey: string;
  icon: string;
  articles: DocArticle[];
}

const DocsPage = () => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  const activeArticleId = searchParams.get("article") || "account-setup";

  const categories: DocCategory[] = useMemo(() => [
    {
      id: "getting-started",
      titleKey: "docs.categories.gettingStarted",
      icon: "https://cdn.dashbal.com/assets/icon_f99624b4.svg",
      articles: [
        {
          id: "account-setup",
          titleKey: "docs.articles.accountSetup",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.accountSetup.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-text-primary dark:text-neutral-100">{t("docs.content.accountSetup.registrationTitle")}</h3>
                <p>{t("docs.content.accountSetup.registrationContent")}</p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-bold text-text-primary dark:text-neutral-100">{t("docs.content.accountSetup.verificationTitle")}</h3>
                <p>{t("docs.content.accountSetup.verificationContent")}</p>
              </div>

              <div className="p-4 bg-accent-primary/5 dark:bg-accent-primary/10 border border-accent-primary/20 rounded-xl flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full bg-accent-primary/10 flex items-center justify-center text-accent-primary">
                   <span className="w-5 h-5 block" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.accountSetup.proTipTitle")}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{t("docs.content.accountSetup.proTipContent")}</p>
                </div>
              </div>
            </div>
          )
        },
        {
          id: "profile-security",
          titleKey: "docs.articles.profileSecurity",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.profileSecurity.intro")}</p>
              <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.profileSecurity.bestPracticesTitle")}</h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                  <div>
                    <p className="font-bold text-sm text-text-primary dark:text-neutral-100">{t("docs.content.profileSecurity.twoFactorTitle")}</p>
                    <p className="text-sm text-neutral-500 font-light">{t("docs.content.profileSecurity.twoFactorContent")}</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                  <div>
                    <p className="font-bold text-sm text-text-primary dark:text-neutral-100">{t("docs.content.profileSecurity.sessionsTitle")}</p>
                    <p className="text-sm text-neutral-500 font-light">{t("docs.content.profileSecurity.sessionsContent")}</p>
                  </div>
                </li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      id: "boards",
      titleKey: "docs.categories.boards",
      icon: "https://cdn.dashbal.com/assets/icon_boards_lamp.svg",
      articles: [
        {
          id: "board-management",
          titleKey: "docs.articles.boardManagement",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.boardManagement.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.boardManagement.createTitle")}</h3>
                <ol className="space-y-3">
                  <li className="text-sm font-light"><span className="font-bold text-text-primary dark:text-neutral-100">1.</span> {t("docs.content.boardManagement.step1")}</li>
                  <li className="text-sm font-light"><span className="font-bold text-text-primary dark:text-neutral-100">2.</span> {t("docs.content.boardManagement.step2")}</li>
                  <li className="text-sm font-light"><span className="font-bold text-text-primary dark:text-neutral-100">3.</span> {t("docs.content.boardManagement.step3")}</li>
                </ol>
              </div>

              <div className="p-4 bg-neutral-50 dark:bg-neutral-800/40 border border-neutral-200 dark:border-neutral-800 rounded-xl">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 italic">
                  {t("docs.content.boardManagement.note")}
                </p>
              </div>
            </div>
          )
        },
        {
          id: "adding-pages",
          titleKey: "docs.articles.addingPages",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.addingPages.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.addingPages.manageTitle")}</h3>
                <ol className="space-y-4">
                  <li className="text-sm font-light">
                    <p className="font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.addingPages.addTitle")}</p>
                    <p>{t("docs.content.addingPages.addContent")}</p>
                  </li>
                  <li className="text-sm font-light">
                    <p className="font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.addingPages.renameTitle")}</p>
                    <p>{t("docs.content.addingPages.renameContent")}</p>
                  </li>
                  <li className="text-sm font-light">
                    <p className="font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.addingPages.reorderTitle")}</p>
                    <p>{t("docs.content.addingPages.reorderContent")}</p>
                  </li>
                </ol>
              </div>
            </div>
          )
        },
        {
          id: "board-usage",
          titleKey: "docs.articles.boardUsage",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.boardUsage.intro")}</p>
              <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.boardUsage.interactionsTitle")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 rounded-xl shadow-sm">
                  <p className="text-sm font-bold mb-1">{t("docs.content.boardUsage.dragTitle")}</p>
                  <p className="text-xs text-neutral-500 font-light">{t("docs.content.boardUsage.dragContent")}</p>
                </div>
                <div className="p-4 bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 rounded-xl shadow-sm">
                  <p className="text-sm font-bold mb-1">{t("docs.content.boardUsage.resizeTitle")}</p>
                  <p className="text-xs text-neutral-500 font-light">{t("docs.content.boardUsage.resizeContent")}</p>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: "connections",
      titleKey: "docs.categories.connections",
      icon: "https://cdn.dashbal.com/assets/icon_channels.svg",
      articles: [
        {
          id: "connecting-shopify",
          titleKey: "docs.articles.connectingShopify",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.connectingShopify.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.connectingShopify.setupTitle")}</h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">1</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingShopify.step1")}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">2</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingShopify.step2")}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">3</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingShopify.step3")}</p>
                  </div>
                </div>
              </div>
            </div>
          )
        },
        {
          id: "connecting-woocommerce",
          titleKey: "docs.articles.connectingWooCommerce",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.connectingWooCommerce.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.connectingWooCommerce.setupTitle")}</h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">1</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingWooCommerce.step1")}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">2</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingWooCommerce.step2")}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">3</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingWooCommerce.step3")}</p>
                  </div>
                </div>
              </div>
            </div>
          )
        },
        {
          id: "connecting-ad-channels",
          titleKey: "docs.articles.connectingAdChannels",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.connectingAdChannels.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.connectingAdChannels.setupTitle")}</h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">1</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingAdChannels.step1")}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">2</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingAdChannels.step2")}</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 text-xs font-bold">3</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 pt-1">{t("docs.content.connectingAdChannels.step3")}</p>
                  </div>
                </div>
              </div>
            </div>
          )
        },
        {
          id: "adding-connection-to-board",
          titleKey: "docs.articles.addingConnectionToBoard",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.addingConnectionToBoard.intro")}</p>
              
              <div className="bg-neutral-50 dark:bg-neutral-800/40 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6 md:p-8 space-y-6 shadow-sm">
                <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-rose-500/20 ring-1 ring-rose-500/50" />
                    <div className="w-3 h-3 rounded-full bg-amber-500/20 ring-1 ring-amber-500/50" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500/20 ring-1 ring-emerald-500/50" />
                  </div>
                  <div className="h-6 w-32 bg-neutral-200 dark:bg-neutral-700 rounded-lg animate-pulse" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-24 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl p-3">
                    <div className="w-8 h-8 bg-neutral-100 dark:bg-neutral-800 rounded mb-2" />
                    <div className="h-2 w-full bg-neutral-100 dark:bg-neutral-800 rounded mb-1" />
                    <div className="h-2 w-2/3 bg-neutral-100 dark:bg-neutral-800 rounded" />
                  </div>
                    <div className="h-24 bg-accent-primary/5 border-2 border-dashed border-accent-primary/30 rounded-xl flex flex-col items-center justify-center gap-2">
                    <div className="w-6 h-6 bg-accent-primary/10 rounded-full flex items-center justify-center">
                       <span className="w-3.5 h-3.5 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_af6592d4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_af6592d4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} />
                    </div>
                    <span className="text-[10px] font-bold text-accent-primary uppercase tracking-tighter">{t("docs.content.addingConnectionToBoard.addConnectionLabel")}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.addingConnectionToBoard.stepsTitle")}</h3>
                <ol className="space-y-4">
                  <li className="text-sm font-light">
                    <p className="font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.addingConnectionToBoard.step1Title")}</p>
                    <p>{t("docs.content.addingConnectionToBoard.step1Content")}</p>
                  </li>
                  <li className="text-sm font-light">
                    <p className="font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.addingConnectionToBoard.step2Title")}</p>
                    <p>{t("docs.content.addingConnectionToBoard.step2Content")}</p>
                  </li>
                  <li className="text-sm font-light">
                    <p className="font-bold text-text-primary dark:text-neutral-100 mb-1">{t("docs.content.addingConnectionToBoard.step3Title")}</p>
                    <p>{t("docs.content.addingConnectionToBoard.step3Content")}</p>
                  </li>
                </ol>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: "charts",
      titleKey: "docs.categories.charts",
      icon: "https://cdn.dashbal.com/assets/icon_f99624b4.svg",
      articles: [
        {
          id: "chart-categories",
          titleKey: "docs.articles.chartCategories",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.chartCategories.intro")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 rounded-xl">
                  <h4 className="font-bold text-sm mb-1">{t("docs.content.chartCategories.financial")}</h4>
                </div>
                <div className="p-4 bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 rounded-xl">
                  <h4 className="font-bold text-sm mb-1">{t("docs.content.chartCategories.marketing")}</h4>
                </div>
                <div className="p-4 bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 rounded-xl">
                  <h4 className="font-bold text-sm mb-1">{t("docs.content.chartCategories.operations")}</h4>
                </div>
                <div className="p-4 bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 rounded-xl">
                  <h4 className="font-bold text-sm mb-1">{t("docs.content.chartCategories.growth")}</h4>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: "billing",
      titleKey: "docs.categories.billing",
      icon: "https://cdn.dashbal.com/assets/icon_journal.svg",
      articles: [
        {
          id: "billing-limits",
          titleKey: "docs.articles.billingLimits",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.billingLimits.intro")}</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.billingLimits.pagesTitle")}</h3>
                <p className="text-sm font-light">{t("docs.content.billingLimits.pagesContent")}</p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.billingLimits.historyTitle")}</h3>
                <p className="text-sm font-light">{t("docs.content.billingLimits.historyContent")}</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: "ai",
      titleKey: "docs.categories.ai",
      icon: "https://cdn.dashbal.com/assets/icon_sync_success.svg",
      articles: [
        {
          id: "talking-to-ai",
          titleKey: "docs.articles.talkingToAi",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.talkingToAi.intro")}</p>
              
              <div className="bg-neutral-900 rounded-2xl p-6 md:p-8 space-y-6 shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-primary to-transparent" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                     <span className="w-5 h-5 text-white/40" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
                  </div>
                  <div className="h-2 w-32 bg-white/5 rounded-full" />
                </div>
                <div className="space-y-3">
                  <div className="h-9 w-full bg-white/5 border border-white/10 rounded-xl px-4 flex items-center justify-between">
                    <span className="text-[11px] text-white/40 font-light italic">{t("docs.content.talkingToAi.inputPlaceholder")}</span>
                    <span className="w-4 h-4 text-white/20" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_af6592d4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_af6592d4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
                  </div>
                </div>
                <div className="flex justify-center">
                   <div className="px-4 py-1.5 bg-accent-primary/20 border border-accent-primary/30 rounded-full text-[10px] font-bold text-accent-primary animate-pulse">
                     {t("docs.content.talkingToAi.activeStatus")}
                   </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.talkingToAi.bannerTitle")}</h3>
                <p className="text-sm font-light leading-relaxed">
                  {t("docs.content.talkingToAi.bannerContent")}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <li className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg text-xs font-medium italic">{t("docs.content.talkingToAi.example1")}</li>
                  <li className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg text-xs font-medium italic">{t("docs.content.talkingToAi.example2")}</li>
                  <li className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg text-xs font-medium italic">{t("docs.content.talkingToAi.example3")}</li>
                  <li className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg text-xs font-medium italic">{t("docs.content.talkingToAi.example4")}</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.talkingToAi.workflowTitle")}</h3>
                <p className="text-sm font-light">
                  {t("docs.content.talkingToAi.workflowContent")}
                </p>
              </div>
            </div>
          )
        },
        {
            id: "plan-limits",
            titleKey: "docs.articles.planLimits",
            content: (
                <div className="space-y-6">
                    <p>{t("docs.content.planLimits.intro")}</p>
                    <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.planLimits.quotaTitle")}</h3>
                    <div className="space-y-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl">
                            <h4 className="text-sm font-bold text-blue-700 dark:text-blue-300 mb-1">{t("docs.content.planLimits.resetTitle")}</h4>
                            <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                                {t("docs.content.planLimits.resetContent")}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm font-light">{t("docs.content.planLimits.usageTitle")}</p>
                            <p className="text-sm text-neutral-500 font-light ml-2">{t("docs.content.planLimits.usageContent")}</p>
                        </div>
                    </div>
                </div>
            )
        }
      ]
    },
    {
      id: "money-journal",
      titleKey: "docs.categories.moneyJournal",
      icon: "https://cdn.dashbal.com/assets/icon_journal.svg",
      articles: [
        {
          id: "creating-money-journal",
          titleKey: "docs.articles.creatingMoneyJournal",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.creatingMoneyJournal.intro")}</p>
              <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.creatingMoneyJournal.setupTitle")}</h3>
              <p className="text-sm font-light leading-relaxed">
                {t("docs.content.creatingMoneyJournal.setupContent")}
              </p>
            </div>
          )
        },
        {
          id: "dropping-files",
          titleKey: "docs.articles.droppingFiles",
          content: (
            <div className="space-y-6">
              <p>{t("docs.content.droppingFiles.intro")}</p>
              <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.droppingFiles.extractionTitle")}</h3>
              <div className="p-6 bg-neutral-900 rounded-2xl text-white space-y-4">
                  <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-widest text-neutral-400">{t("docs.content.droppingFiles.engineTitle")}</span>
                  </div>
                  <div className="space-y-2">
                      <p className="text-lg font-display">{t("docs.content.droppingFiles.extractsTitle")}</p>
                      <div className="flex flex-wrap gap-2">
                          {[t("docs.content.droppingFiles.tag1"), t("docs.content.droppingFiles.tag2"), t("docs.content.droppingFiles.tag3"), t("docs.content.droppingFiles.tag4"), t("docs.content.droppingFiles.tag5")].map(tag => (
                              <span key={tag} className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-bold">{tag}</span>
                          ))}
                      </div>
                  </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: "specs",
      titleKey: "docs.categories.specs",
      icon: "https://cdn.dashbal.com/assets/icon_f99624b4.svg",
      articles: [
          {
              id: "understanding-specs",
              titleKey: "docs.articles.understandingSpecs",
              content: (
                  <div className="space-y-6">
                      <p>{t("docs.content.understandingSpecs.intro")}</p>
                      <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100">{t("docs.content.understandingSpecs.casesTitle")}</h3>
                      <ul className="space-y-3">
                          <li className="text-sm font-light"><code className="bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-accent-primary">{t("docs.content.understandingSpecs.case1")}</code></li>
                          <li className="text-sm font-light"><code className="bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-accent-primary">{t("docs.content.understandingSpecs.case2")}</code></li>
                      </ul>
                  </div>
              )
          }
      ]
    }
  ], [t]);


  const activeArticle = useMemo(() => {
    return categories
      .flatMap(c => c.articles)
      .find(a => a.id === activeArticleId) || categories[0].articles[0];
  }, [categories, activeArticleId]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categories;
    return categories.map(cat => ({
      ...cat,
      articles: cat.articles.filter(art => 
        t(art.titleKey).toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(cat => cat.articles.length > 0);
  }, [categories, searchQuery, t]);

  return (
    <div className="h-screen w-full flex bg-background-page dark:bg-neutral-950 overflow-hidden font-sans">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:flex lg:flex-col w-64 shrink-0 border-r border-neutral-200 dark:border-neutral-800/40 bg-white dark:bg-neutral-900">
        <div className="h-18 flex items-center px-6 shrink-0 border-b border-neutral-100 dark:border-neutral-800/40">
          <div
            onClick={() => navigate("/boards")}
            className="group flex items-center gap-3 px-3 py-2 -ml-3 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-all duration-300 active:scale-[0.98]"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-accent-primary/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <Logo className="w-7 h-7 relative z-10 transition-transform duration-300 group-hover:scale-110" />
            </div>
            <span className="text-xl font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight group-hover:text-accent-primary transition-colors">
              {t("brandName")}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-6 space-y-8 custom-scrollbar">
          {/* Search bar */}
          <div className="relative px-2">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
            <input
              type="text"
              placeholder={String(t("docs.searchPlaceholder"))}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-10 pr-4 bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 rounded-lg text-xs font-medium focus:ring-2 focus:ring-accent-primary/20 transition-all outline-none"
            />
          </div>

          <div className="space-y-7">
            {filteredCategories.map((cat) => (
              <div key={cat.id} className="space-y-2">
                <div className="flex items-center gap-2 px-3 mb-2">
                  <span className="text-[9px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
                    {t(cat.titleKey)}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {cat.articles.map((art) => (
                    <button
                      key={art.id}
                      onClick={() => {
                        setSearchParams({ article: art.id });
                        setFeedbackGiven(false);
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-200 ${
                        activeArticleId === art.id
                          ? "bg-accent-primary/5 dark:bg-accent-primary/10 text-accent-primary font-bold shadow-sm"
                          : "text-neutral-500 dark:text-neutral-400 hover:text-text-primary dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                      }`}
                    >
                      <div className="flex items-center justify-center w-5 h-5 shrink-0">
                        <div className={`w-1 h-1 rounded-full transition-colors ${
                          activeArticleId === art.id 
                            ? "bg-accent-primary shadow-[0_0_8px_rgba(241,90,36,0.4)]" 
                            : "bg-neutral-300 dark:bg-neutral-700"
                        }`} />
                      </div>
                      <span className="truncate">{t(art.titleKey)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-neutral-900">
        {/* Header */}
        <header className="h-18 flex items-center justify-between px-6 border-b border-neutral-100 dark:border-neutral-800/40 sticky top-0 z-10 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"
            >
              <span className="w-6 h-6 block" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
            </button>
            <h1 className="text-base font-bold text-text-primary dark:text-neutral-100 tracking-tight">
              {t("docs.title")}
            </h1>
          </div>
          <button
            onClick={() => navigate("/boards")}
            className="h-8 px-4 text-xs font-bold bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 rounded-lg hover:opacity-90 transition-all active:scale-95 shadow-sm flex items-center gap-2"
          >
            <span className="w-3.5 h-3.5 block" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_ffed198c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_ffed198c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
            {t("docs.backToHome")}
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
            <AnimatePresence mode="wait">
              <motion.article
                key={activeArticleId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="prose dark:prose-invert max-w-none"
              >
                <div className="mb-10">
                  <h2 className="text-3xl font-bold font-display text-text-primary dark:text-neutral-100 mb-2 tracking-tight">
                    {t(activeArticle.titleKey)}
                  </h2>
                  <p className="text-sm text-neutral-400 dark:text-neutral-500 font-light">
                    {t("docs.lastUpdated", { 
                      date: new Date().toLocaleDateString(i18n.language, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      }) 
                    })}
                  </p>
                </div>
                
                <div className="text-text-primary dark:text-neutral-300 leading-relaxed font-light text-base lg:text-lg">
                  {activeArticle.content}
                </div>
                
                {/* Feedback Section */}
                <div className="mt-20 pt-10 border-t border-neutral-100 dark:border-neutral-800">
                    <div className="flex flex-col items-center gap-4">
                        <div className="relative h-10 w-full flex items-center justify-center overflow-hidden">
                            <AnimatePresence mode="wait">
                                {!feedbackGiven ? (
                                    <motion.div
                                        key="feedback-prompt"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="flex items-center gap-4"
                                    >
                                        <span className="text-sm font-semibold text-text-primary dark:text-neutral-100">
                                            {t("docs.wasHelpful")}
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setFeedbackGiven(true)}
                                                className="h-8 px-4 bg-accent-primary text-white text-xs font-bold rounded-lg hover:opacity-90 transition-all active:scale-95"
                                            >
                                                {t("docs.yes")}
                                            </button>
                                            <button
                                                onClick={() => setFeedbackGiven(true)}
                                                className="h-8 px-4 border border-neutral-200 dark:border-neutral-800 text-text-primary dark:text-neutral-100 text-xs font-bold rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all active:scale-95"
                                            >
                                                {t("docs.no")}
                                            </button>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="thanks-msg"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex items-center gap-2 text-accent-primary"
                                    >
                                        <span className="w-4 h-4 block" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_sync_success.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_sync_success.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
                                        <span className="text-sm font-bold">
                                            {t("docs.thanksFeedback")}
                                        </span>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-neutral-900 z-50 lg:hidden shadow-2xl"
            >
                {/* Mobile sidebar content same as desktop but with logo in header */}
                <div className="h-full flex flex-col">
                    <div className="h-18 flex items-center justify-between px-6 border-b border-neutral-100 dark:border-neutral-800/40">
                        <div
                            onClick={() => {
                                navigate("/boards");
                                setIsSidebarOpen(false);
                            }}
                            className="group flex items-center gap-3 px-3 py-2 -ml-3 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-all duration-300 active:scale-[0.98]"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-accent-primary/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <Logo className="w-7 h-7 relative z-10 transition-transform duration-300 group-hover:scale-110" />
                            </div>
                            <span className="text-xl font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight group-hover:text-accent-primary transition-colors">
                                {t("brandName")}
                            </span>
                        </div>
                        <button onClick={() => setIsSidebarOpen(false)} className="p-2 -mr-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800">
                            <span className="w-5 h-5 block" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_725495b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_725495b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor' }} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-3 py-6 space-y-8">
                        {/* Same menu content */}
                        <div className="relative px-2 mb-6">
                            <input
                            type="text"
                            placeholder={String(t("docs.searchPlaceholder"))}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full h-9 pl-10 pr-4 bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 rounded-lg text-xs font-medium outline-none"
                            />
                        </div>
                        <div className="space-y-7">
                            {filteredCategories.map((cat) => (
                            <div key={cat.id} className="space-y-2">
                                <div className="flex items-center gap-2 px-3 mb-2">
                                <span className="text-[9px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">{t(cat.titleKey)}</span>
                                </div>
                                <div className="space-y-0.5">
                                {cat.articles.map((art) => (
                                    <button
                                    key={art.id}
                                    onClick={() => {
                                        setSearchParams({ article: art.id });
                                        setFeedbackGiven(false);
                                        setIsSidebarOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-200 ${
                                        activeArticleId === art.id 
                                            ? "bg-accent-primary/5 dark:bg-accent-primary/10 text-accent-primary font-bold shadow-sm" 
                                            : "text-neutral-500 dark:text-neutral-400 hover:text-text-primary dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                                    }`}
                                    >
                                        <div className="flex items-center justify-center w-5 h-5 shrink-0">
                                            <div className={`w-1 h-1 rounded-full transition-colors ${
                                                activeArticleId === art.id 
                                                    ? "bg-accent-primary shadow-[0_0_8px_rgba(241,90,36,0.4)]" 
                                                    : "bg-neutral-300 dark:bg-neutral-700"
                                            }`} />
                                        </div>
                                        <span className="truncate">{t(art.titleKey)}</span>
                                    </button>
                                ))}
                                </div>
                            </div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocsPage;
